import GridContainer, { GridOffset } from "@insite/mobius/GridContainer";
import React, { MouseEventHandler, useEffect } from "react";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import translate from "@insite/client-framework/Translate";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Select from "@insite/mobius/Select";
import { css } from "styled-components";
import { CreditCardDtoCollection, updateSavedCreditCards } from "@NA/Services/SavedCreditCardsService";
import Button, { ButtonPresentationProps, ButtonWrapper } from "@insite/mobius/Button";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import ADIColors from "@NA/Utils/ADIColors";
import { FormFieldStyle } from "@insite/mobius/FormField";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { FileUpload, LazyImage, Link } from "@insite/mobius/index";
import CustomModel from "@NA/Components/ADICustomModal";
 
export interface AdiEditCreditCardModalStyles {
    uploadProductListGridItem:GridItemProps;
    subHeadings?: TypographyProps;
    subDescription?: TypographyProps;
    filesAndProductText: TypographyProps;
    fileuploadStyledWrapper?: InjectableCss;
    uploadStyledWrapper?: InjectableCss;
    fileuploadedStyledWrapper?: InjectableCss;
    actionGridItem?: GridItemProps;
}
 
export const adiEditCreditCardModalStyles: AdiEditCreditCardModalStyles = {
    uploadProductListGridItem: {
        width: 12,
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    subHeadings: {
        css: css`
            color: ${ADIColors.darkBlue1};
            font-size: 24px;
            line-height: normal;
            margin-top: 0;
            font-weight: bold !important;
        `,
    },
    subDescription: {
        css: css`
            margin-top: 10px;
            margin-bottom: 10px;
        `,
    },
    filesAndProductText: {
        css: css`
            color: ${ADIColors.danger} !important;
            text-align: left !important;
            font-weight: 600 !important;
            letter-spacing: -0.06px;
        `,
    },
    fileuploadStyledWrapper: {
        css: css`
            width: 100%;
            max-height: 100px;
            padding: 0 !important;
            height: 100px;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            border: 1px dashed ${ADIColors.gray10};
            background-color: ${ADIColors.white};
            position: relative;
            max-width: 817px;
            margin: 0 auto;
        `,
    },
    uploadStyledWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            ${FormFieldStyle} {
                display: none !important;
            }
            ${ButtonWrapper} {
                background-color: transparent;
                border: none;
                margin: 0;
                padding: 0;
                margin-top: 3px;
                padding-left: 5px;
                > ${TypographyStyle} {
                    margin-left: 5px;
                }
                > span {
                    color: ${getColor("primary")};
                    font-size: 16px;
                    font-weight: 800;
                    margin: 0;
                    padding: 0;
                }
                :hover {
                    background-color: ${ADIColors.white};
                    border: none;
                }
            }
            > ${StyledWrapper} {
                display: flex;
                justify-content: center;
                align-items: center;
            }
        `,
    },
    fileuploadedStyledWrapper: {
        css: css`
            width: 100%;
            max-height: 100px;
            padding: 0 !important;
            height: 100px;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: ${ADIColors.white};
            text-align: center;
            border: 1px dashed ${ADIColors.gray10};
            position: relative;
            max-width: 817px;
            margin: 0 auto;
            font-size: 14px;
        `,
    },
    actionGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: end;
            margin-top: 27px;
        `,
    },
};
 
const styles = adiEditCreditCardModalStyles;
 
interface OwnProps {
    isOpen:boolean;
    closeQuoteModal:any;
    invalidMessage:string | string[];
    formErrors:any;
    handleDrop:any;
    handleDragOver:any;
    handleDragLeave:any;
    handleDragStart:any;
    onAddFile:any;
    files:any;
    onRemoveFile:any;
    setDropzone:any;
    handleImportAndReview: any;
}
 
type Props = OwnProps;
 
const ADIInvoiceHistoryImportModal = ({
    isOpen,
    closeQuoteModal,
    invalidMessage,
    formErrors,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleDragStart,
    onAddFile,
    files,
    onRemoveFile,
    setDropzone,
    handleImportAndReview
 
}: Props) => {
    useEffect(() => {setDropzone(document.getElementById('dropzone'))}, []);
    return (
        <CustomModel
            headline={'Upload Open Invoices'}
            isOpen={isOpen}
            errorStyle={false}
            handleClose={closeQuoteModal}
        >
            <>
            <GridItem width={12} {...styles.uploadProductListGridItem}>
                        <Typography as="div" {...styles.subDescription}>
                            {translate("You can upload one single CSV file with multiple Invoices. If you don't have the file, please ")} 
                            <Typography color="blue">
                                <Link href="https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Import/Import_invoice.csv">{translate(" click here  ")}</Link>&nbsp;
                            </Typography> 
                                {translate("to download a template to create your CSV file.",)}
                        </Typography>
                        <Typography {...styles.filesAndProductText}>{formErrors.filesAndProduct}</Typography>
                        {Array.isArray(invalidMessage) ? invalidMessage.map(message => {
                            return <><Typography {...styles.filesAndProductText}>{message}</Typography></>
                        })  : invalidMessage && <Typography {...styles.filesAndProductText}>{invalidMessage}</Typography>}
                        <StyledWrapper {...styles.fileuploadStyledWrapper}>
                            <StyledWrapper
                                {...styles.uploadStyledWrapper}
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                id="dropzone"
                            >
                                <StyledWrapper draggable={true} onDragStart={handleDragStart}>
                                    <LazyImage
                                        width="50px"
                                        height="auto"
                                        src="https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/icons/drop-files.png"
                                    />
                                    <Typography>{translate("Drop files to attach, or")}</Typography>
                                    <FileUpload buttonText="browse." onChange={onAddFile} />
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                        {files.length > 0 && (
                            <StyledWrapper {...styles.fileuploadedStyledWrapper}>
                                <GridContainer>
                                    {files.map((item:any) => (
                                        <GridItem width={4} key={item.name}>
                                            <Typography>
                                                {item.name}{" "}
                                                <Link as="a" onClick={() => onRemoveFile(item.name)}>
                                                    {" "}
                                                    Remove
                                                </Link>
                                            </Typography>
                                        </GridItem>
                                    ))}
                                </GridContainer>
                            </StyledWrapper>
                        )}
                    </GridItem>
                    <GridItem width={12} {...styles.actionGridItem}>
                        <Button type="button" onClick={closeQuoteModal} variant="tertiary" style={{marginRight:'5px'}}>
                            {translate("Cancel")}
                        </Button>
                        <Button type="button" variant="primary" onClick={handleImportAndReview} disabled={files.length === 0} >
                            {translate("Import and Review")}
                        </Button>
                    </GridItem>
            </>
        </CustomModel>
    );
};
 
export default ADIInvoiceHistoryImportModal;
 